import {
  buildPersonName,
  isNotNullNotUndefined,
} from "@repo/app-types/helpers/utilities";
import type { FC } from "react";
import { signoutUser } from "../../api/supabaseService.js";
import {
  selectCompany,
  selectIsUsingSSO,
} from "../../features/company/companySlice.js";
import { endServiceAccess } from "../../features/serviceAccess/serviceAccessClient.js";
import {
  checkIsServiceAccessEndingSoon,
  formatServiceAccessTimeLeft,
  useServiceAccessMsLeft,
} from "../../features/serviceAccess/serviceAccessTimeLeft.js";
import {
  selectTeammate,
  selectUserSession,
} from "../../features/teammate/teammateSlice.js";
import { getWorkspaceAlias } from "../../helpers/workspaceAlias.js";
import { useAppSelector } from "../../hooks/hook.js";
import {
  UserProfileWidget,
  type UserProfileMenuItem,
} from "./userProfileWidget.js";

function buildUserProfileMenuItems(
  isUsingSSO: boolean,
  isServiceSession: boolean,
): readonly UserProfileMenuItem[] {
  return [
    { key: "settings", label: "Settings", href: "/settings/profile" },
    isUsingSSO
      ? undefined
      : {
          key: "invite-teammates",
          label: "Invite teammates",
          href: "/settings/teammates",
        },
    {
      key: "knowledge-center",
      label: "Knowledge center",
      href: "https://help.alpharun.com/",
      opensInNewWindow: true,
    },
    {
      key: "contact-support",
      label: "Contact support",
      href: "mailto:support@alpharun.com",
      opensInNewWindow: true,
    },
    // A service session ends from its own section instead.
    isServiceSession
      ? undefined
      : {
          key: "sign-out",
          label: "Sign out",
          onAction: () => void signoutUser(),
        },
  ].filter(isNotNullNotUndefined);
}

export const UserProfileWidgetContainer: FC = () => {
  const teammate = useAppSelector(selectTeammate);
  const isUsingSSO = useAppSelector(selectIsUsingSSO);
  const company = useAppSelector(selectCompany);
  const session = useAppSelector(selectUserSession);
  // The session's cap is the only end the client knows ahead of time.
  const msLeft = useServiceAccessMsLeft(
    session?.type === "service" ? session.expires_at : null,
  );

  if (!teammate) {
    return null;
  }

  const serviceAccess =
    msLeft !== null && company !== null
      ? {
          companyName: company.name,
          workspaceAlias: getWorkspaceAlias(company.id),
          timeLeft: formatServiceAccessTimeLeft(msLeft),
          isEndingSoon: checkIsServiceAccessEndingSoon(msLeft),
          onEnd: () => void endServiceAccess(),
        }
      : undefined;

  return (
    <UserProfileWidget
      // Without the service teammate's "(Alpharun)" suffix: only the employee
      // sees this, beside the service access pill that already says so.
      name={buildPersonName(teammate) ?? teammate.email}
      email={teammate.email}
      menuItems={buildUserProfileMenuItems(
        isUsingSSO,
        session?.type === "service",
      )}
      serviceAccess={serviceAccess}
    />
  );
};
